
import React, { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import LayerFolder from "./components/LayerFolder";
import Folder from "./components/Folder";
import LayerScope from "./components/LayerScope";
import LayerLink from "./components/LayerLink";

import { ServerTypeHelper } from "../../_config/helpers";

import "./LayerSwitcher.css";

let folderIndex = 0;

const LayerSwitcher = (props) => {
    const { t } = useTranslation();

    const [layerTree, setLayerTree] = useState(null);
    const [hasVisibleLayers, setHasVisibleLayers] = useState(false);

    const sortAlphabetically = (tree) =>
        [...tree]
            .sort((a, b) =>
                (a.title || "").localeCompare(
                    b.title || "",
                    "pt-BR",
                    {
                        sensitivity: "base",
                    }
                )
            )
            .map((folder) => ({
                ...folder,
                subFolder: folder.subFolder
                    ? sortAlphabetically(folder.subFolder)
                    : null,
            }));



    const layerSeqs = useMemo(() => {
        if (!props.map) {
            return new Set();
        }

        const seq = new Set();

        props.map
            .getLayers()
            .getArray()
            .forEach((layer) => {
                const folderSequence =
                    layer.get &&
                    layer.get("folderSequence");

                if (folderSequence) {
                    seq.add(folderSequence);
                }
            });

        return seq;
    }, [props.map]);



    const updateHasVisibleLayers = () => {
        if (!props.map) {
            return;
        }

        const anyVisible = props.map
            .getLayers()
            .getArray()
            .some(
                (layer) =>
                    layer.get("serverType") ===
                    ServerTypeHelper.GEOSERVER &&
                    layer.getVisible()
            );

        setHasVisibleLayers(anyVisible);
    };

    useEffect(() => {
        if (!props.map) {
            return;
        }

        const layers = props.map
            .getLayers()
            .getArray();

        const listeners = layers.map((layer) => {
            const fn = () => updateHasVisibleLayers();

            layer.on("change:visible", fn);

            return {
                layer,
                fn,
            };
        });

        updateHasVisibleLayers();

        return () => {
            listeners.forEach(({ layer, fn }) => {
                layer.un(
                    "change:visible",
                    fn
                );
            });
        };
    }, [props.map]);


    const folderHasVisibleLayer = (
        folderSequence
    ) => {
        if (!props.map) {
            return false;
        }

        return props.map
            .getLayers()
            .getArray()
            .some(
                (layer) =>
                    layer
                        .get("folderSequence")
                        ?.startsWith(
                            folderSequence
                        ) &&
                    layer.getVisible()
            );
    };



    const buildFolderTree = (tree) =>
        tree
            .map((folder) => {
                if (folder.subFolder == null) {
                    if (
                        !layerSeqs.has(
                            folder.folderSequence
                        )
                    ) {
                        return null;
                    }

                    folderIndex += 1;

                    return (
                        <LayerFolder
                            key={`folder.${folderIndex}`}
                            folderSequence={
                                folder.folderSequence
                            }
                            title={folder.title}
                            map={props.map}
                            folderIndex={folderIndex}
                            hasNewVisibleLayer={
                                props.hasNewVisibleLayer
                            }
                            sidebarVisible={
                                props.visible
                            }
                        />
                    );
                }


                folderIndex += 1;

                const hasLayersHere =
                    layerSeqs.has(
                        folder.folderSequence
                    );


                const subFolderHasLayers = (
                    subFolders
                ) =>
                    subFolders.some((sub) => {
                        if (
                            sub.subFolder == null
                        ) {
                            return layerSeqs.has(
                                sub.folderSequence
                            );
                        }

                        return (
                            layerSeqs.has(
                                sub.folderSequence
                            ) ||
                            subFolderHasLayers(
                                sub.subFolder
                            )
                        );
                    });



                if (
                    !hasLayersHere &&
                    !subFolderHasLayers(
                        folder.subFolder
                    )
                ) {
                    return null;
                }


                return (
                    <Folder
                        key={`folder.${folderIndex}`}
                        title={folder.title}
                        map={props.map}
                        folderIndex={folderIndex}
                        sidebarVisible={
                            props.visible
                        }
                        hasVisibleLayer={
                            folderHasVisibleLayer(
                                folder.folderSequence
                            )
                        }
                    >
                        {hasLayersHere
                            ? (() => {
                                const innerIndex =
                                    ++folderIndex;

                                return (
                                    <LayerFolder
                                        key={`folder.${innerIndex}`}
                                        folderSequence={
                                            folder.folderSequence
                                        }
                                        title="..."
                                        map={props.map}
                                        folderIndex={
                                            innerIndex
                                        }
                                        hasNewVisibleLayer={
                                            props.hasNewVisibleLayer
                                        }
                                        inline={true}
                                        activeVisible={
                                            props.activeVisible
                                        }
                                    />
                                );
                            })()
                            : null}

                        {buildFolderTree(
                            folder.subFolder
                        )}
                    </Folder>
                );
            })
            .filter(Boolean);


    useEffect(() => {
        if (
            !props.map ||
            !props.explorer
        ) {
            return;
        }


        folderIndex = 0;

        const sorted =
            sortAlphabetically(
                props.explorer
            );

        setLayerTree(
            buildFolderTree(sorted)
        );
    }, [
        props.map,
        props.explorer,
        layerSeqs,
    ]);



    const deactivateVisibleLayers = () => {
        if (!props.map) {
            return;
        }

        props.map
            .getLayers()
            .forEach((layer, i) => {
                if (
                    layer.getVisible() &&
                    layer.get("serverType") ===
                    ServerTypeHelper.GEOSERVER
                ) {
                    layer.setVisible(false);

                    const checkbox =
                        document.getElementById(
                            "layer" + i
                        );

                    if (checkbox) {
                        checkbox.checked = false;
                    }

                    if (
                        props.hasNewVisibleLayer
                    ) {
                        props.hasNewVisibleLayer(
                            -i
                        );
                    }
                }
            });

        updateHasVisibleLayers();
    };



    return (
        <div
            className={`LayerSwitcher ${props.visible
                    ? "LayerSwitcher--visible"
                    : "LayerSwitcher--hidden"
                }`}
        >
            <div className="LayerSwitcherExplorer">


                <div className="LayerSwitcherHeader">



                    <div className="LayerSwitcherHeaderContent">

                        <i
                            className="fa-solid fa-layer-group LayerSwitcherHeaderIcon"
                            aria-hidden="true"
                        />

                        <div>
                            <span className="LayerSwitcherHeaderTitle">
                                Explorador de Dados
                            </span>


                        </div>

                    </div>

                    <button
                        type="button"
                        className="CloseBtnHeader"

                        onClick={
                            props.onClose
                        }
                        aria-label="Fechar explorador"
                    >
                        <i
                            className="fa-solid fa-xmark"
                            aria-hidden="true"
                        />
                    </button>

                </div>

                <div className="LayerSwitcherScope">

                    <LayerScope
                        currentLanguage={
                            props.currentLanguage
                        }
                        map={props.map}
                    />

                    <span className="LayerSwitcherTitle">
                        {t(
                            "layerSwitcher.categories"
                        )}
                    </span>

                </div>


                <div className="LayerTree">

                    {layerTree}

                </div>


                <div className="LayerSwitcherFooter">

                    <LayerLink
                        content={t(
                            "layerSwitcher.disableVisible"
                        )}
                        checked={
                            hasVisibleLayers
                        }
                        onToggle={
                            deactivateVisibleLayers
                        }
                    />

                </div>

            </div>
        </div>
    );
};

export default LayerSwitcher;

